import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
  onOpenQuestionInBank?: (row: UnifiedQuestionRow) => void;
}
export function QuestionDetailPanel({
  row,
  onOpenQuestionInBank
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5631" data-mp="12y42wnc7gblm">
      <div className="mb-4 flex justify-end" data-id="element-5632" data-mp="29deg9s87lsma">
        <Button variant="secondary" size="sm" onClick={() => onOpenQuestionInBank?.(row)} disabled={!onOpenQuestionInBank} data-id="element-5633" data-mp="29epjnqxqsycv">
          
          <ExternalLink className="w-4 h-4 mr-1.5" data-id="element-5634" data-mp="2vm1rrokxfqw4" />
          Open in Vragenbank
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5635" data-mp="2qmozupf77tmo">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5636" data-mp="2lncgwfrg76pd">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5637" data-mp="2lmauxg8w8zsv">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5638" data-mp="2gr58mgbkxj49" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5639" data-mp="27wqx8ou66sqf">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5640" data-mp="27uwcpbmt92mv" />
              <p className="text-sm text-gray-600" data-id="element-5641" data-mp="22xxkiympj5rb">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5642" data-mp="1szglrfhj1mup">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5643" data-mp="1t1kmjok4q5qr" data-mpi={idx} />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5644" data-mp="1ed17yp1aeuo7">
          {/* Quality & Metadata */}
          <div data-id="element-5645" data-mp="1ef45du4rrq2y">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5646" data-mp="1jbm6mk0w56aj">
              <Brain className="w-3.5 h-3.5" data-id="element-5647" data-mp="15cuv95dexyq3" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5648" data-mp="1aawb7hfjlhxc">
              <div className="grid grid-cols-2 gap-4" data-id="element-5649" data-mp="1y0qo1yzv36hl">
                <div data-id="element-5650" data-mp="1y1a93okt6azu">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5651" data-mp="1y2v0x6fqnfu0">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5652" data-mp="221u6mt9angmi">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5653" data-mp="1t9icoxez1b1s">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5654" data-mp="1t8qvirm4ffv1">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5655" data-mp="2gz3321dgfal3">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5656" data-mp="2gxrwjtsmjoqa">
                      
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5657" data-mp="2v2s7zmb141e4">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5658" data-mp="34u6gukovedjx">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5659" data-mp="2zvv33yvgfiwf">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5660" data-mp="2zwmxj6dbfal4">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5661" data-mp="286ryuj3ki8js">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5662" data-mp="2m4glnwh73prh">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5663" data-mp="2m5rp1v6qavi2">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5664" data-mp="3sjqwoupq8uoz">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5665" data-mp="3skagkgyxtt5n">
              <Database className="w-3.5 h-3.5" data-id="element-5666" data-mp="225j84hlipuv7" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5667" data-mp="2c1manoka7vio">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5668" data-mp="2g37whzesnk50" />
                <span className="text-sm text-gray-600" data-id="element-5669" data-mp="2g4j0o0c5itwn">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5670" data-mp="2q1dva85b0a5n">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5671" data-mp="2pzax30tzzapu" data-mpi={i}>
                
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5672" data-mp="33ttv702wrs2c">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5673" data-mp="33udbydgtoaes">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5674" data-mp="38psnuzv9rw8k" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5675" data-mp="x5thz9uc95j1">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5676" data-mp="s445dprgwjsu">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5677" data-mp="o4mwemcu14ix" />
                  <span className="text-sm" data-id="element-5678" data-mp="j5a9k1lynxgc">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5679" data-mp="1fu83jhlvzrcg">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5680" data-mp="1bupm4vt9xyed" />
                  <span className="text-sm" data-id="element-5681" data-mp="1726ba6lz1hd9">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5682" data-mp="1zjs9mkalalgs">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5683" data-mp="1zkbk5fxwu7ks">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5684" data-mp="1uqfmyfph0oqo" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5685" data-mp="2dqluu0ywlygu">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5686" data-mp="1v0dutrqi3o4v">
                      <div data-id="element-5687" data-mp="1zyp7ea86kcqs">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5688" data-mp="1zurljgu7x737">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5689" data-mp="1lz60tlv0p9gl">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5690" data-mp="3gbnga79m75ao">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5691" data-mp="18p46gwb392ku">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5692" data-mp="18qfamx8g4cch">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5693" data-mp="14m7ieq2s4e9i">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5694" data-mp="1dogv9mlisjq2">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5695" data-mp="1sc1u34bd3ws6">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5696" data-mp="1nes6lu7j9v6z">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5697" data-mp="1wf6un1bvtoe6">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5698" data-mp="1wfzsfcsv53k6" />
                        <span className="text-sm text-gray-600" data-id="element-5699" data-mp="711qfp353ggz">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5700" data-mp="3dbf1h01rf5qe">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5701" data-mp="3hbz0681zehen" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5702" data-mp="3haxlltlrbcs6" />}
                        <span className="text-sm text-gray-600" data-id="element-5703" data-mp="3m48nrhfmj5dr">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}